import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetchSearch, SearchFetchError, type SearchKind } from '../../../api/search';
import { useDebouncedValue } from '../../../hooks/use-debounced-value';

import { FILTERED_LIMIT, GROUP_PREVIEW_LIMIT, isSearchableTerm } from './search-kinds';

const TYPING_PAUSE_MS = 200;

/** A refusal or a bad term won't change on a second try; an outage might. */
function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof SearchFetchError && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * Searches once typing pauses. Earlier results stay in place while the next
 * ones load, so the list never flashes empty between keystrokes.
 */
export function useGlobalSearch(term: string, kind: SearchKind | null) {
  const settledTerm = useDebouncedValue(term.trim(), TYPING_PAUSE_MS);
  return useQuery({
    queryKey: ['search', settledTerm, kind],
    queryFn: ({ signal }) =>
      fetchSearch(
        {
          q: settledTerm,
          type: kind ?? undefined,
          limit: kind ? FILTERED_LIMIT : GROUP_PREVIEW_LIMIT,
        },
        { signal },
      ),
    enabled: isSearchableTerm(settledTerm),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: shouldRetry,
  });
}
